import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Modal,
  ModalProps,
  Typography,
} from '@duxton-web/ui';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';

import { getTitle } from '@/utils/changelog';

interface JsonDiffViewerProps {
  value?: any;
  oldValue: string;
  newValue: string;
  title?: string;
}

const DiffViewerModal = ({
  value,
  open,
  onCancel,
  oldValue,
  newValue,
  title,
}: ModalProps & JsonDiffViewerProps) => {
  const [showDiffOnly, setShowDiffOnly] = useState<boolean>(true);

  return (
    <Modal
      title={
        <ConfigProvider
          theme={{ components: { Typography: { titleMarginBottom: 0 } } }}
        >
          <Typography.Title level={3}>
            {title ? title : getTitle(value)}
          </Typography.Title>
        </ConfigProvider>
      }
      centered
      open={open}
      width="calc(100vw - 300px)"
      maskClosable={false}
      onCancel={(e) => {
        setShowDiffOnly(true);

        onCancel(e);
      }}
      footer={
        <Button
          type="default"
          size="small"
          onClick={(e) => {
            setShowDiffOnly(true);

            onCancel(e as any);
          }}
        >
          Close
        </Button>
      }
      styles={{
        body: {
          height: 'calc(100vh - 200px)',
        },
      }}
      data-testid="diff-modal"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Checkbox
            checked={showDiffOnly}
            onChange={(e) => setShowDiffOnly(e.target.checked)}
            title="Show Diff Only"
          >
            Show Diff Only
          </Checkbox>
        </div>
        <div style={{ display: 'flex' }}>
          <Typography.Text type="danger" style={{ flexBasis: '50%' }}>
            Before
          </Typography.Text>
          <Typography.Text type="success" style={{ flexBasis: '50%' }}>
            After
          </Typography.Text>
        </div>
        <div style={{ flexGrow: 1 }}>
          <ReactDiffViewer
            styles={{
              diffContainer: {
                lineBreak: 'anywhere',
              },
              line: {
                maxWidth: 600,
              },
              content: {
                fontSize: 12,
                maxWidth: 300,
                width: '45%',
              },
            }}
            oldValue={oldValue}
            newValue={newValue}
            splitView
            showDiffOnly={showDiffOnly}
            compareMethod={DiffMethod.WORDS}
          />
        </div>
      </div>
    </Modal>
  );
};

const JsonDiffViewer = ({
  value,
  oldValue,
  newValue,
  title,
}: JsonDiffViewerProps) => {
  const [showDiffOnly, setShowDiffOnly] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxHeight: 460,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            gap: 4,
          }}
        >
          <Checkbox
            title="Show Diff Only"
            checked={showDiffOnly}
            onChange={(e) => setShowDiffOnly(e.target.checked)}
          >
            Show Diff Only
          </Checkbox>
          <Button type="secondary" size="small" onClick={() => setOpen(true)}>
            Expand View
          </Button>
        </div>
        <div
          style={{
            flexGrow: 1,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          <ReactDiffViewer
            styles={{
              diffContainer: {
                lineBreak: 'anywhere',
                fontSize: 12,
              },
              line: {
                maxWidth: 600,
              },
              content: {
                // fontSize: 12,
                maxWidth: 300,
                width: '45%',
              },
              codeFold: {
                fontSize: 12,
              },
            }}
            showDiffOnly={showDiffOnly}
            oldValue={oldValue}
            newValue={newValue}
            splitView
            compareMethod={DiffMethod.WORDS}
          />
        </div>
      </div>
      <DiffViewerModal
        value={value}
        open={open}
        onCancel={() => setOpen(false)}
        oldValue={oldValue}
        newValue={newValue}
        title={title}
      />
    </>
  );
};

export default JsonDiffViewer;
